// src/components/TableWrapper.jsx - ACTUALIZADO CON BOTÓN DINÁMICO
import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'react-bootstrap';
import { FaEye, FaEdit, FaTrash, FaDownload, FaUndo } from 'react-icons/fa';

// Renderizador de acciones (actualizado para detectar estado)
function ActionsRenderer(props) {
  const { onView, onEdit, onDelete, onDownload, tableType } = props;
  const data = props.data || {};

  // ✅ DETECTAR SI LA FILA ESTÁ DADA DE BAJA (funciona para personas y obras)
  const isDeactivated = data.deletedDate !== null && data.deletedDate !== undefined;

  // Estilo compacto para botones
  const buttonStyle = {
    padding: '2px',
    margin: '0 2px',
    lineHeight: 1,
    border: 'none',
    minWidth: '24px',
    minHeight: '24px',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: '100%' }}>
      {onView && (
        <Button
          variant="outline-primary"
          size="sm"
          style={buttonStyle}
          onClick={() => onView(data)}
          title="Ver detalles"
        >
          <FaEye size={13} />
        </Button>
      )}
      {onEdit && (
        <Button
          variant="outline-success"
          size="sm"
          style={buttonStyle}
          onClick={() => onEdit(data)}
          title="Editar"
        >
          <FaEdit size={13} />
        </Button>
      )}
      {/* ✅ BOTÓN DINÁMICO: Eliminar/Dar de baja O Restaurar */}
      {onDelete && (
        <Button
          variant={isDeactivated ? "outline-success" : "outline-danger"}
          size="sm"
          style={buttonStyle}
          onClick={() => onDelete(data)}
          title={isDeactivated ? "Restaurar" : (tableType === 'obras' ? "Finalizar" : "Dar de baja")}
        >
          {isDeactivated ? <FaUndo size={13} /> : <FaTrash size={13} />}
        </Button>
      )}
      {onDownload && tableType === 'documentos' && (
        <Button
          variant="outline-info"
          size="sm"
          style={buttonStyle}
          onClick={() => onDownload(data)}
          title="Descargar"
        >
          <FaDownload size={13} />
        </Button>
      )}
    </div>
  );
}

/**
 * Componente wrapper para AG Grid
 */
const TableWrapper = ({
  columnDefs = [],
  rowData = [],
  tableType = 'personas',
  onView,
  onEdit,
  onDelete,
  onDownload,
  customGridOptions = {},
  style,
  ...props
}) => {
  // Verificamos si ya hay una columna de acciones definida
  const hasActionsColumn = columnDefs.some(col =>
    col.headerName === 'Opciones'
  );

  // Procesamos las definiciones de columnas para aplicar el filtro de selección múltiple
  // donde se ha especificado useSetFilter: true
  const processedColumnDefs = columnDefs.map(colDef => {
    if (colDef.useSetFilter === true) {
      return {
        ...colDef,
        filter: 'agSetColumnFilter', // Cambiamos explícitamente el tipo de filtro
        filterParams: {
          // Parámetros específicos para el filtro de selección múltiple
          values: true, // Detectar automáticamente valores únicos
          buttons: ['apply', 'reset', 'clear'],
          closeOnApply: true,
          // Mantener el campo de búsqueda en el filtro para valores grandes
          suppressMiniFilter: false,
          // Mantener el filtro flotante si estaba habilitado
          suppressFilterButton: false
        },
        // Mantener el filtro flotante si estaba activado
        floatingFilter: colDef.floatingFilter !== false
      };
    }

    return colDef;
  });

  // Si hay handlers para acciones y no se ha proporcionado una columna de acciones,
  // añadimos una columna de acciones automáticamente
  let enhancedColumnDefs = [...processedColumnDefs];

  if (!hasActionsColumn && (onView || onEdit || onDelete || (onDownload && tableType === 'documentos'))) {
    enhancedColumnDefs = [
      {
        headerName: 'Opciones',
        field: 'id',
        sortable: false,
        filter: false,
        width: 110,
        pinned: 'left',
        cellRenderer: (params) => {
          return (
            <ActionsRenderer
              data={params.data}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onDownload={onDownload}
              tableType={tableType}
            />
          );
        }
      },
      ...processedColumnDefs
    ];
  }

  // Tamaño de fila reducido
  const rowHeight = 26;

  // Configuración predeterminada de columnas con mejoras para filtros
  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    minWidth: 100,
    suppressMenu: true,
    filterParams: {
      closeOnApply: true,
      debounceMs: 200
    },
    ...(customGridOptions.defaultColDef || {})
  };

  return (
    <div
      className="ag-theme-alpine"
      style={{
        width: '100%',
        height: 'auto', // Auto altura para adaptarse al contenido
        borderRadius: '8px', // Bordes redondeados como en el módulo de personas
        overflow: 'hidden',
        border: '1px solid #dee2e6',
        ...style
      }}
    >
      <AgGridReact
        // Datos y definiciones de columnas
        columnDefs={enhancedColumnDefs}
        rowData={rowData}

        // Configuración de columnas por defecto
        defaultColDef={defaultColDef}

        // Ajustamos el tamaño de las filas y encabezados
        rowHeight={rowHeight}
        headerHeight={40}

        // Configuración de paginación - aumentamos el tamaño de página
        pagination={true}
        paginationPageSize={25}

        // Animaciones
        animateRows={true}

        // Habilitamos los filtros flotantes
        floatingFilter={true}

        // Configuración para que la altura se ajuste al contenido
        domLayout='autoHeight'

        // Configuración para el manejo de selección en filtros
        suppressSetFilterPartialStateSaving={false}

        // Permite seleccionar y copiar el texto de las celdas (arrastrar + Ctrl+C)
        enableCellTextSelection={true}
        ensureDomOrder={true}

        // Otras opciones personalizadas
        {...customGridOptions}
        {...props}
      />
    </div>
  );
};

export default TableWrapper;
